import React, { useContext } from 'react';
import { Badge, Col, Row, Space, Typography } from 'antd';

import AmazonWebServicesContext from '@totem/components/administration/amazonWebServices/AmazonWebServicesContext';
import AmazonWebServicesTable from '@totem/components/administration/amazonWebServices/AmazonWebServicesTable';
import AssignDeviceModal from '@totem/components/administration/amazonWebServices/AssignDeviceModal';
import AssignHierarchyModal from '@totem/components/administration/amazonWebServices/AssignHierarchyModal';
import AssignSalesOrderModal from '@totem/components/administration/amazonWebServices/AssignSalesOrderModal';
import AwsCostByHierarchyTable from '@totem/components/administration/amazonWebServices/AwsCostByHierarchyTable';
import AwsCostByStatusTable from '@totem/components/administration/amazonWebServices/AwsCostByStatusTable';
import AwsMonthlyCostChart from '@totem/components/administration/amazonWebServices/AwsMonthlyCostChart';
import DeleteInstanceModal from '@totem/components/administration/amazonWebServices/DeleteInstanceModal';
import UpdateStatusModal from '@totem/components/administration/amazonWebServices/UpdateStatusModal';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import IBStatistic from '@totem/components/ui_components/widgets/statistic/IBStatistic';

const formatCurrency = (value: string, currency: string) => {
  const amount = Number.parseFloat(value);
  if (Number.isNaN(amount)) {
    return `${value} ${currency}`;
  }

  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  });
};

export const AmazonWebServicesExtra = () => {
  const { data } = useContext(AmazonWebServicesContext);

  return (
    <Space>
      <Badge count={data.length} overflowCount={999} offset={[0, -7]}>
        Instances&nbsp;&nbsp;
      </Badge>
    </Space>
  );
};

const CostTab = () => (
  <>
    <Typography.Title level={5}>Monthly Cost (Last 12 Months)</Typography.Title>
    <AwsMonthlyCostChart />
    <Typography.Title level={5} style={{ marginTop: 24 }}>
      Cost by Organization
    </Typography.Title>
    <AwsCostByHierarchyTable />
    <Typography.Title level={5} style={{ marginTop: 24 }}>
      Cost by Status
    </Typography.Title>
    <AwsCostByStatusTable />
  </>
);

const AmazonWebServicesLayout = () => {
  const { summary, data } = useContext(AmazonWebServicesContext);

  // Only counts instances still in the default "Discovered" state — once a
  // person has explicitly triaged an instance (Internal Use, Ignored, etc.)
  // it's no longer "forgotten," even without a sales order tied to it.
  const unassignedRunningCount = data.filter(
    (record) =>
      record.item.instance.state === 'running' &&
      record.item.salesOrders.length === 0 &&
      record.status === 'Discovered',
  ).length;

  const tabItems = [
    {
      key: 'details',
      label: 'Details',
      children: <AmazonWebServicesTable />,
    },
    {
      key: 'cost',
      label: 'Cost',
      children: <CostTab />,
    },
  ];

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <IBStatistic
            title="Instances"
            subTitle={<span>&nbsp;</span>}
            value={summary.totalInstances}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <IBStatistic
            title="Cost (Current Month)"
            subTitle={<span>&nbsp;</span>}
            value={formatCurrency(summary.currentMonthCost, summary.currency)}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <IBStatistic
            title="Cost (Previous Month)"
            subTitle={<span>&nbsp;</span>}
            value={formatCurrency(summary.previousMonthCost, summary.currency)}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <IBStatistic
            title="Unassigned Running Instances"
            subTitle={<span>No sales orders tied to this instance</span>}
            value={unassignedRunningCount}
            valueStyle={
              unassignedRunningCount > 0
                ? { color: 'var(--ant-color-error)' }
                : undefined
            }
          />
        </Col>
      </Row>
      <IBTabs items={tabItems} />
      <AssignHierarchyModal />
      <AssignSalesOrderModal />
      <AssignDeviceModal />
      <UpdateStatusModal />
      <DeleteInstanceModal />
    </>
  );
};

export default AmazonWebServicesLayout;
